import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { saveAs } from 'file-saver';
import { forkJoin, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { PlanningFile } from '../../../classes/planning-file';
import { PlanningHasEntity } from '../../../classes/planning-has-entity.class';
import { Planning } from '../../../classes/planning.class';
import { ImageModalComponent } from '../../../generic/components/image-modal/image-modal.component';
import { Person, PersonService } from '../../../services/person.service';
import { PlanningService } from '../../../services/planning/planning.service';
import { LocalStorage } from '../../../storage.class';

export interface TracklineOrderFormDialogData {
    planningId: number;
}

@Component({
    selector: 'app-trackline-order-form',
    standalone: false,
    templateUrl: './trackline-order-form.component.html',
    styleUrl: './trackline-order-form.component.scss',
})
export class TracklineOrderFormComponent implements OnInit, OnDestroy {
    planning: Planning;
    person: Person | null = null;
    planningHasList: PlanningHasEntity[] = [];
    numberOfEmployees = 0;

    isDialog: boolean;

    window = window;

    private subscriptions = new Subscription();

    constructor(
        private planningService: PlanningService,
        private dialog: MatDialog,
        private personService: PersonService,
        @Optional() private route: ActivatedRoute,
        @Optional() public dialogRef: MatDialogRef<TracklineOrderFormComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: TracklineOrderFormDialogData
    ) {
        this.isDialog = !!dialogRef;
    }

    ngOnInit(): void {
        const planningId = this.isDialog ? this.dialogData.planningId : +this.route.snapshot.params.planningId;

        this.subscriptions.add(
            this.planningService
                .getPlanningFull(planningId)
                .pipe(
                    switchMap(planning => {
                        const personId = planning.data?.person_id;
                        return forkJoin({
                            planning: of(planning),
                            person: personId ? this.personService.get(personId) : of(null),
                        });
                    })
                )
                .subscribe(({ planning, person }) => {
                    this.planning = planning;
                    this.person = person;
                    this.planningHasList = (planning.planning_has ?? []).filter(
                        p => p.id !== planning.planning_has_id && p.entitytype_key !== 'hiringStaff'
                    );
                    this.numberOfEmployees =
                        (planning.user_planning?.length ?? 0) +
                        (planning.planning_has?.filter(p => !!p.driver_user_id)?.length ?? 0);
                    this.print();
                })
        );
    }

    get selectedCustomerName(): string {
        return this.planning?.customer?.name ?? this.planning?.contractor ?? '-';
    }

    print() {
        if (!this.isDialog && this.route?.snapshot.queryParams.print === 'true') {
            setTimeout(() => {
                window.print();
                window.close();
            });
        }
    }

    close(): void {
        this.dialogRef?.close();
    }

    private getFile(planningFileId: number, cb: (data: Blob) => void) {
        this.subscriptions.add(
            this.planningService.getFile(planningFileId).subscribe(cb, error => {
                console.error('Error downloading file', error);
            })
        );
    }

    openFile(file: PlanningFile) {
        const fileName = file.name?.toLowerCase() ?? '';
        const isPdf = fileName.endsWith('.pdf');
        const isImage = /\.(jpe?g|png|gif|svg)$/.test(fileName);

        if (!isPdf && !isImage) {
            if (file.id) {
                this.downloadFile(file);
            }
            return;
        }

        if (!file.file) {
            return;
        }

        const url = `${environment.apiEndpoint}file/planning/${file.file}?access_token=${LocalStorage.getUserToken()}`;

        this.dialog.open(ImageModalComponent, {
            panelClass: 'image-viewer',
            width: '99vw',
            maxWidth: '1300px',
            height: '99vh',
            minHeight: '500px',
            maxHeight: '99vh',
            hasBackdrop: true,
            data: {
                viewIndex: 0,
                ...(isPdf ? { singlePdf: url } : { images: [url] }),
            },
        });
    }

    downloadFile(file: PlanningFile) {
        this.getFile(file.id, data => saveAs(data, file.name));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
